import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import identityApi from '../../clients/IdentityClient';
import { type CreateBookingRequest } from '../../models/booking/CreateBookingRequest';
import { type TimeSlotAvailability } from '../../models/booking/TimeSlotAvailability';
import { useAuth } from '../../context/AuthContext';
import { BookingProvider, useBookingContext } from './BookingContext';
import PageBreadcrumb from '../shared/PageBreadcrumb';
import ActivitiesSection from './sections/ActivitiesSection';
import DateTimeSection from './sections/DateTimeSection';
import LocationSection from './sections/LocationSection';
import ReviewSection from './sections/ReviewSection';
import SimulatorSection from './sections/SimulatorSection';
import { calculateRequiredCredits } from '../../utils/bookingUtils';
import { savePreferredLocationIfEmpty } from '../../utils/preferredLocation';
import './BookingCreate.css';

const BookingCreateBody: React.FC = () => {
  const { booking, resetBooking, updateBooking } = useBookingContext();
  const { user, balance, refreshBalance, refreshUserProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isReviewMode, setIsReviewMode] = useState(() => searchParams.get('review') === '1');
  const [daySlots, setDaySlots] = useState<TimeSlotAvailability[]>([]);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [bookableDates, setBookableDates] = useState<Set<string> | null>(null);
  const [viewMonth, setViewMonth] = useState<{ year: number; month: number }>(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() + 1 };
  });
  const [submission, setSubmission] = useState<{ status: 'idle' | 'loading' | 'success' | 'error'; message?: string }>({
    status: 'idle'
  });

  const handleViewMonthChange = useCallback((year: number, month: number) => setViewMonth({ year, month }), []);

  const availableCredits = balance ?? 0;

  useEffect(() => {
    const { location, activityId } = booking;
    if (!location || !activityId) {
      setBookableDates(null);
      return;
    }
    let cancelled = false;
    const loadMonth = async () => {
      // Fetch bookable dates for the currently visible calendar month. Refetches whenever
      // the user navigates to a different month via onViewMonthChange → setViewMonth.
      try {
        const res = await identityApi.getMonthAvailability(
          location, viewMonth.year, viewMonth.month, activityId
        );
        if (!cancelled) setBookableDates(new Set(res.bookableDates));
      } catch {
        if (!cancelled) setBookableDates(null); // degrade: no disabling
      }
    };
    loadMonth();
    return () => { cancelled = true; };
  }, [booking.location, booking.activityId, viewMonth.year, viewMonth.month]);

  // Clear a selected date that the new activity no longer offers.
  useEffect(() => {
    if (bookableDates && booking.date && !bookableDates.has(booking.date)) {
      updateBooking({ date: '', startTime: '', endTime: '' });
    }
  }, [bookableDates, booking.date, updateBooking]);

  useEffect(() => {
    if (!booking.location || !booking.date) {
      setDaySlots([]);
      setSlotsError('');
      return;
    }

    const loadSlots = async () => {
      setIsSlotsLoading(true);
      setSlotsError('');
      try {
        const response = await identityApi.getDayAvailability(booking.location, booking.date, booking.activityId);
        setDaySlots(response.slots ?? []);
      } catch {
        setDaySlots([]);
        setSlotsError('Failed to load availability');
      } finally {
        setIsSlotsLoading(false);
      }
    };

    loadSlots();
  }, [booking.location, booking.date, booking.activityId]);

  const requiredCredits = calculateRequiredCredits(
    booking.activities,
    booking.activityId,
    booking.fullMotionSimulators,
    booking.esportsSimulators
  );
  const missingCredits = Math.max(0, requiredCredits - availableCredits);

  const totalSimulators = booking.fullMotionSimulators + booking.esportsSimulators;
  const canReview = Boolean(
    booking.location &&
    booking.activityId &&
    totalSimulators > 0 &&
    booking.date &&
    booking.startTime &&
    missingCredits <= 0
  );

  const firstDriver = booking.additionalDrivers[0];
  const areDriversValid = Boolean(
    booking.additionalDrivers.length === totalSimulators &&
    firstDriver?.firstName.trim() &&
    firstDriver?.lastName.trim() &&
    firstDriver?.email.trim() &&
    booking.additionalDrivers.slice(1).every(d => d.firstName.trim())
  );

  const canSubmit = canReview && areDriversValid;
  const isSubmittingOrDone = submission.status === 'loading' || submission.status === 'success';

  const onConfirm = async () => {
    if (!canSubmit || !booking.activityId) return;

    setSubmission({ status: 'loading' });

    const correlationId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const tickets = Array.from({ length: totalSimulators }, (_, index) => {
      const driver = booking.additionalDrivers[index];
      return {
        driverId: user?.email === driver?.email ? user?.driverId ?? null : null,
        simulatorType: index < booking.fullMotionSimulators ? 'FullMotion' : 'Esports',
        firstName: driver?.firstName ?? '',
        lastName: driver?.lastName ?? '',
        email: driver?.email ?? ''
      };
    });

    const request: CreateBookingRequest = {
      location: booking.location,
      date: booking.date,
      startTime: booking.startTime,
      endTime: booking.endTime,
      activityId: booking.activityId,
      correlationId,
      tickets
    };

    try {
      await identityApi.CreateBooking(request);
      await savePreferredLocationIfEmpty(
        user?.preferredLocation, booking.location,
        (loc) => identityApi.patchUser(loc),
        refreshUserProfile,
      );
      await refreshBalance();
      navigate('/thank-you?returnTo=dashboard');
      resetBooking();
    } catch {
      setSubmission({ status: 'error', message: 'Failed to create booking. Please try again.' });
    }
  };

  return (
    <>
      <PageBreadcrumb items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Book a race' }]} />
      <div className="container py-4">
        <div className="d-flex flex-column gap-3">
          {!isReviewMode ? (
            <div className="booking-create-content">
              <div className="row g-4">
                <div className="col-12 col-lg-5">
                  <div className="d-flex flex-column gap-3">
                    <LocationSection />
                    <ActivitiesSection />
                    <SimulatorSection slots={daySlots} isLoadingSlots={isSlotsLoading} />
                  </div>
                </div>
                <div className="col-12 col-lg-7">
                  <DateTimeSection slots={daySlots} isLoadingSlots={isSlotsLoading} slotsError={slotsError} bookableDates={bookableDates} onViewMonthChange={handleViewMonthChange} />
                </div>
              </div>

              <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
                {requiredCredits > 0 && (
                  <p className="text-paragraphs mb-0">
                    <span className="settings-field-label me-1">Cost:</span>
                    <strong>
                      {requiredCredits}{' '}
                      <img src="/img/coin.png" alt="credits" style={{ height: '1em', verticalAlign: 'middle', marginRight: 2 }} />
                    </strong>
                    {missingCredits === 0
                      ? ` (${availableCredits - requiredCredits} remaining)`
                      : <>
                        <span className="text-danger ms-1">({missingCredits} more needed) </span>
                        <Link
                          to={`/checkout?returnTo=booking`}
                          className="alert-link"
                        >
                          Buy more here
                        </Link>
                      </>
                    }
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary booking-wizard-nav-button"
                  onClick={() => setIsReviewMode(true)}
                  disabled={!canReview || isSubmittingOrDone}
                >
                  Review
                </button>
              </div>
            </div>
          ) : (

            <div className="booking-create-content d-flex flex-column gap-3">
              <ReviewSection />
              {submission.message && (
                <div className={`alert ${submission.status === 'success' ? 'alert-success' : 'alert-danger'} text-white mb-0`} role="alert">
                  {submission.message}
                </div>
              )}
              {slotsError && (
                <div className="alert alert-danger mb-0" role="alert">{slotsError}</div>
              )}
              <button
                type="button"
                className="btn btn-success booking-wizard-nav-button"
                onClick={onConfirm}
                disabled={!canSubmit || isSubmittingOrDone}
              >
                {submission.status === 'loading' ? 'Creating...' : 'Confirm'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary "
                onClick={() => setIsReviewMode(false)}
                disabled={isSubmittingOrDone}
              >
                Back
              </button>

            </div>
          )}
        </div>
      </div>
    </>
  );
};

const BookingCreate: React.FC = () => (
  <BookingProvider>
    <BookingCreateBody />
  </BookingProvider>
);

export default BookingCreate;
